import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getInsightContentTypeDisplayText } from 'components/insightContent/utils/utils'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
// @ts-ignore
import { Tooltip, Dialog } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import AccordionSectionType from 'components/insightContent/enum/AccordionSectionType'
import {
  TopInfoBox,
  TopInfoRight,
  StyledTermContent
} from 'components/insightContent/visualization/InfoBox/index.css'
import { CountTypeStrings } from 'enums/CountType'
import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { InsightContentSettingType } from 'components/insightContent/types'
import AccordionGroup from 'components/insightContent/visualization/AccordionGroup'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { AutomationId } from 'utils/automationUtil'
import { sendTypedMessage } from 'utils/iframeUtil'
import { toExternal } from 'vo/util'
import GoToAnalysisButton from 'components/insightContent/visualization/GoToAnalysisButton'
import messageUtil from 'utils/messageUtil'
import InsightDialog from 'components/insightContent/visualization/InsightDialog'
import { InsightObjective } from '../../types'
import useViewOptionName from './hooks'
import {
  StyledVersus,
  StyledSubtitleContainer,
  StyledTopicPivotDescription,
  StyledTopicPivotName,
  StyledTooltip
} from './index.css'
import FormatedName from './FormatedName'

const Name = styled.span`
  font-weight: ${sys.fontWeight.semibold};
`

const DIALOG_CONTENT_HEIGHT = '460px'

const resolveSelectionTooltip = (
  insightObjective: InsightObjective,
  viewOption: InsightViewOptionStrings
) => {
  const { insight, name } = insightObjective
  const formattedName = <FormatedName name={name} viewOption={viewOption} />

  if (insight.variants && insight.variants.length > 0) {
    return `${name}, ${insight.variants.join(', ')}`
  }

  return <StyledTooltip>{formattedName}</StyledTooltip>
}

const hasPivot = (insightObjective: InsightObjective) =>
  insightObjective.themeName !== undefined ||
  insightObjective.tagName !== undefined ||
  insightObjective.docName !== undefined

const InsightModal = ({
  open,
  title,
  countType,
  viewOption,
  selection,
  onClose,
  detailSetting,
  timeUnits,
  forceNotScale,
  widgetSettingWithSelection
}: {
  open: boolean
  title: string | undefined
  countType: CountTypeStrings
  viewOption: InsightViewOptionStrings
  selection: InsightObjective[] | undefined
  onClose: () => void
  detailSetting: InsightContentSettingType | undefined
  timeUnits?: MetricTimeUnitStrings[]
  forceNotScale?: boolean
  widgetSettingWithSelection?: any
}) => {
  const theme = useDisplayTheme() as Theme
  const [displayTopicTooltip, setDisplayTopicTooltip] = useState(false)
  const [displayPivotTooltip, setDisplayPivotTooltip] = useState(false)
  const { t } = useTranslation()
  const viewOptionName = useViewOptionName(viewOption)

  const checkTopicContent = (
    setTooltipTriggerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => (StyledNameContainerRef: HTMLDivElement) => {
    if (!StyledNameContainerRef) {
      return
    }

    setDisplayTopicTooltip(
      StyledNameContainerRef.scrollHeight! > StyledNameContainerRef.clientHeight!
    )
    setTooltipTriggerRef(StyledNameContainerRef)
  }

  const checkPivotContent = (
    setTooltipTriggerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => (StyledNameContainerRef: HTMLDivElement) => {
    if (!StyledNameContainerRef) {
      return
    }

    setDisplayPivotTooltip(
      StyledNameContainerRef.scrollHeight! > StyledNameContainerRef.clientHeight!
    )
    setTooltipTriggerRef(StyledNameContainerRef)
  }

  function goToAnalysis() {
    sendTypedMessage('goToAnalysis', toExternal(widgetSettingWithSelection))
  }

  const resolvePivotTooltip = (insightObjective: InsightObjective) => {
    if (insightObjective.themeName) {
      return insightObjective.themeName
    }

    if (insightObjective.tagName) {
      return insightObjective.tagName
    }

    if (insightObjective.docName) {
      return insightObjective.docName
    }

    return ''
  }

  const resolveVersus = () =>
    displayTopicTooltip ? (
      <>
        <StyledVersus mode={theme}>{t('and')}</StyledVersus>&nbsp;
      </>
    ) : (
      <>
        &nbsp;<StyledVersus mode={theme}>{t('and')}</StyledVersus>&nbsp;
      </>
    )

  if (!selection || selection.length === 0) {
    return null
  }

  const insightObjective = selection?.[0]
  const { topicName, themeName, tagName, docName, name } = insightObjective

  return (
    <InsightDialog open={open} onClose={onClose}>
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <TopInfoBox mode={theme}>
        <StyledSubtitleContainer>
          <StyledTopicPivotDescription>
            <Tooltip
              mode={theme}
              enabled={displayTopicTooltip}
              callbackRefCreator={checkTopicContent}
              content={topicName}>
              <StyledTopicPivotName
                testId={AutomationId.insightModal.selectedTopic}
                hasMaxWidth={hasPivot(insightObjective)}
                mode={theme}>{`${t('topic')}: ${topicName}`}</StyledTopicPivotName>
            </Tooltip>
            {hasPivot(insightObjective) ? resolveVersus() : ''}
            <Tooltip
              mode={theme}
              enabled={displayPivotTooltip}
              callbackRefCreator={checkPivotContent}
              content={resolvePivotTooltip(insightObjective)}>
              <StyledTopicPivotName
                testId={AutomationId.insightModal.selectedPivot}
                hasMaxWidth
                mode={theme}>
                {themeName ? `${t('theme')}: ${themeName}` : ''}
                {tagName ? `${t('tag')}: ${tagName}` : ''}
                {docName ? `${t('product')}: ${docName}` : ''}
              </StyledTopicPivotName>
            </Tooltip>
          </StyledTopicPivotDescription>
          <Tooltip
            mode={theme}
            enabled
            content={resolveSelectionTooltip(insightObjective, viewOption)}>
            <StyledTermContent mode={theme}>
              {`${viewOptionName}: `}
              <FormatedName name={name} viewOption={viewOption} />
            </StyledTermContent>
          </Tooltip>
        </StyledSubtitleContainer>
        <TopInfoRight>
          <GoToAnalysisButton onClick={() => goToAnalysis()} />
        </TopInfoRight>
      </TopInfoBox>
      <Dialog.DialogContent height={DIALOG_CONTENT_HEIGHT}>
        <AccordionGroup
          timeUnits={timeUnits}
          forceNotScale={forceNotScale}
          countType={countType}
          insightContentsConfig={[
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SUMMARY),
              accordionContents: [
                {
                  insightContentType: InsightContentType.TOTAL_MEASURE,
                  detailSetting
                },
                {
                  insightContentType: InsightContentType.NAME_MEASURE,
                  detailSetting,
                  renderNameMeasureTitleContent: () => (
                    <>
                      <Name>
                        <FormatedName name={name} viewOption={viewOption} />
                      </Name>
                      &nbsp;
                      {messageUtil.countType(countType)}
                    </>
                  )
                }
              ]
            },
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SOUND_BITE),
              accordionContents: [
                {
                  insightContentType: InsightContentType.SOUND_BITE,
                  detailSetting
                }
              ]
            }
          ]}
        />
      </Dialog.DialogContent>
      <Dialog.DialogActions />
    </InsightDialog>
  )
}

export default InsightModal
